import React, { useEffect, useState } from 'react';
import {
    DocumentCard,
    DocumentCardDetails,
    DocumentCardType,
    IDocumentCardStyles,
    Image,
    ITextStyles,
    Stack,
    StackItem,
    Text,
} from '@fluentui/react';
import { FontIcon } from '@fluentui/react/lib/Icon';
import GoogleMapsMapComponent from '../google-maps/google-maps-map.component';
import GoogleMapMarkerBasicComponent from '../map-marker/google-map-marker-basic.component';
import moment from 'moment';
import ButtonPrimaryRoundedComponent from '../button-primary-rounded/button-primary-rounded.component';
import ButtonWhiteComponent from '../button-white/button-white.component';
import ButtonBlueComponent from '../button-blue/button-blue.component';
import WorkPartCreateComponent from '../work-part/work-part-create.component';
import WorkDocumentListComponent from '../work-document/work-document-list.component';
import WorkDocumentCreateComponent from '../work-document/work-document-create.component';
import WorkPartFinishComponent from '../work-part/work-part-finish.component';
import WorkPartFinishInfoComponent from '../work-part/work-part-finish-info.component';
import ErrorMessageComponent from '../error-message/error-message.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemBandejaEntradaActuacion from '../../models/ItemBandejaEntradaActuacion';
import ItemTarea from '../../models/ItemTarea';
import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ItemDocumento from '../../models/ItemDocumento';

import playIcon from '../../assets/img32/play_32.png';
import pauseIcon from '../../assets/img32/pause_32.png';

const documentCardStyles : IDocumentCardStyles = {
    root: {
        maxWidth: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: "#cccccc",
        padding: '5px 10px',
        height: 'auto',
        overflow: 'hidden',
        boxShadow: '1px 1px 5px 0px rgba(0,0,0,0.25);',
        marginBottom: 10,
    },
};

const titleTextStyles : ITextStyles = {
    root: {
        color: '#000000',
    },
};

const getGoogleLatLng = (value?: string) : google.maps.LatLngLiteral => {
    if (value) {
        if (value.includes(',')) {
            const valueArray : string[] = value.split(',');
            return {
                lat: parseFloat(valueArray[0]),
                lng: parseFloat(valueArray[1]),
            };
        }
        else {
            const valueArray : string[] = value.split(';');
            return {
                lat: parseFloat(valueArray[0]),
                lng: parseFloat(valueArray[1]),
            };
        }
    }
    else {
        return {
            lat: 0,
            lng: 0,
        };
    }
};

const monthName = (month: number) : string => {
    switch(month) {
        case 0: return "ENE";
        case 1: return "FEB";
        case 2: return "MAR";
        case 3: return "ABR";
        case 4: return "MAY";
        case 5: return "JUN";
        case 6: return "JUL";
        case 7: return "AGO";
        case 8: return "SEP";
        case 9: return "OCT";
        case 10: return "NOV";
        case 11: return "DIC";
        default: return "";
    }
};

const padLeft = (num: number, size: number) : string => {
    let s : string = num.toString();
    while (s.length < size) {
        s = "0" + s;
    }
    return s;
};

const duration = (startDate: string, endDate?: string) : string => {
    if (endDate) {
        let diff : number = moment(endDate).diff(moment(startDate), 'minutes');
        let hours : number = Math.floor(diff / 60);
        let minutes : number = diff % 60;
        return `${padLeft(hours, 2)}:${padLeft(minutes, 2)} h`;
    }
    else {
        let diff : number = moment().diff(moment(startDate), 'minutes');
        let hours : number = Math.floor(diff / 60);
        let minutes : number = diff % 60;
        return `${padLeft(hours, 2)}:${padLeft(minutes, 2)} h`;
    }
};

interface IWorkReadonlyComponentProps {
    item: ItemBandejaEntradaActuacion;
    work: ItemTarea;
    workParts: ItemRegistroTiempo[];
    setLoading: (value: boolean) => void;
    onWorkPartCreatedOrEdited: () => void;
    onWorkPartDeleted: () => void;
    onWorkFinished: () => void;
};

const WorkReadonlyComponent = (props: IWorkReadonlyComponentProps) => {
    const auth = useAuthStatus();

    const [unfinishedWorkPart, setUnfinishedWorkPart] = useState<ItemRegistroTiempo>(); 
    const [workPartOpen, setWorkPartOpen] = useState<boolean>(false);
    const [selectedWorkPart, setSelectedWorkPart] = useState<ItemRegistroTiempo>();
    const [workDocumentListOpen, setWorkDocumentListOpen] = useState<boolean>(false);
    const [workDocumentCreateOpen, setWorkDocumentCreateOpen] = useState<boolean>(false);
    const [workDocuments, setWorkDocuments] = useState<ItemDocumento[]>([]);
    const [workPartFinishOpen, setWorkPartFinishOpen] = useState<boolean>(false);
    const [workPartFinishInfoOpen, setWorkPartFinishInfoOpen] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const setLoading = props.setLoading;

    useEffect(() => {
        setUnfinishedWorkPart(props.workParts.find((wp: ItemRegistroTiempo) => {
            return (wp.Inicio && !wp.Fin);
        }));
    }, [props.workParts]);

    useEffect(() => {
        setLoading(true);
        if (auth.user) {
            ApiService.Post<ItemDocumento[]>("OrdenesTrabajo", "GetDocumentosAdjuntos", auth.user, {
                "IdOrden": props.work.IdOrden,
                "IdTarea": props.item.IdTarea,
                "Filtro": "",
                "IdElemento": 1100,
            }).then((response: ItemDocumento[]) => {
                setWorkDocuments(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    }, [props.work.IdOrden, props.item.IdTarea, auth.user, setLoading]);

    const onWorkPartDelete = (workPart: ItemRegistroTiempo) : void => {
        if (auth.user) {
            if (window.confirm(`¿Confirma que desea eliminar el trabajo?`)) {
                setLoading(true);
                ApiService.Post<void>(`OrdenesTrabajo`, `DeleteRegistroTiempos`, auth.user, {
                    "Id": workPart.Id,
                }).then(() => {
                    props.onWorkPartDeleted();
                    setLoading(false);
                }, (error: string) => {
                    setError(error);
                    setLoading(false);
                });
            }
        }
    };

    const onWorkPartEdit = (workPart: ItemRegistroTiempo) : void => {
        setSelectedWorkPart(Object.assign({}, workPart));
    };

    const onWorkDocumentCreatedOrEdited = () : void => {
        setLoading(true);
        if (auth.user) {
            ApiService.Post<ItemDocumento[]>("OrdenesTrabajo", "GetDocumentosAdjuntos", auth.user, {
                "IdOrden": props.work.IdOrden,
                "IdTarea": props.item.IdTarea,
                "Filtro": "",
                "IdElemento": 1100,
            }).then((response: ItemDocumento[]) => {
                setWorkDocuments(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    };

    return (
        <>
            <Stack 
                className="p-2 position-relative"
                styles={{
                    root: {
                        backgroundColor: '#F5F5F5',
                    },
                }}>
                <StackItem>
                    <DocumentCard
                        styles={documentCardStyles}
                        type={DocumentCardType.compact}>
                        <DocumentCardDetails>
                            <Text styles={titleTextStyles}>
                                {props.item.Referencia || props.item.IdTarea} |
                                <div 
                                    style={{ 
                                        display: 'inline-block', 
                                        marginLeft: 5,
                                    }} 
                                    dangerouslySetInnerHTML={{ __html: props.item.Descripcion }}></div>
                            </Text>
                        </DocumentCardDetails>
                    </DocumentCard>
                </StackItem>
                <StackItem>
                    <GoogleMapsMapComponent
                        zoom={props.work.PosicionGPS ? 13 : 3}
                        center={getGoogleLatLng(props.work.PosicionGPS)}>
                        {
                            props.work.PosicionGPS ? (
                                <GoogleMapMarkerBasicComponent position={getGoogleLatLng(props.work.PosicionGPS)} />
                            ) : (
                                null
                            )
                        }
                    </GoogleMapsMapComponent>
                    <Stack 
                        className='position-absolute'
                        styles={{
                            root: {
                                left: 19,
                                top: 110,
                            },
                        }}>
                        <StackItem 
                            className='text-center px-3'
                            styles={{
                                root: {
                                    backgroundColor: 'var(--color-primario)',
                                },
                            }}>
                            <FontIcon 
                                aria-label="Clock" 
                                iconName="Clock"
                                className='text-white'
                                style={{
                                    fontSize: 20,
                                }} />
                        </StackItem>
                        <StackItem
                            className='px-3 py-1 text-center'
                            styles={{
                                root: {
                                    backgroundColor: '#4A4748',
                                },
                            }}>
                            <Text 
                                className='text-white'
                                styles={{
                                    root: {
                                        fontSize: 20,
                                    },
                                }}>
                                {moment(props.work.FechaHoraInicioPrevista).format('DD')}
                            </Text>
                        </StackItem>
                        <StackItem
                            className='px-3 py-1 text-center'
                            styles={{
                                root: {
                                    backgroundColor: '#4A4748',
                                },
                            }}>
                            <Text 
                                className='text-white'
                                styles={{
                                    root: {
                                        fontSize: 14,
                                    },
                                }}>
                                {monthName(moment(props.work.FechaHoraInicioPrevista).month())}
                            </Text>
                        </StackItem>
                        <StackItem
                            className='px-3 py-1 text-center'
                            styles={{
                                root: {
                                    backgroundColor: '#4A4748',
                                },
                            }}>
                            <Text 
                                className='text-white'
                                styles={{
                                    root: {
                                        fontSize: 14,
                                    },
                                }}>
                                {moment(props.work.FechaHoraInicioPrevista).format("HH:MM")}
                            </Text>
                        </StackItem>
                    </Stack>
                </StackItem>
                <StackItem 
                    className='my-2'>
                    <Stack
                        horizontal={true}>
                        <StackItem 
                            className='flex-fill rounded p-2'
                            styles={{
                                root: {
                                    backgroundColor: 'var(--color-primario)',
                                },
                            }}>
                            <Text className='text-white'>
                                {props.item.NombreDireccion}
                                <br/>
                                {props.item.DireccionCompleta}
                            </Text>
                        </StackItem>
                        <StackItem
                            className='rounded p-2 ms-2'
                            styles={{
                                root: {
                                    backgroundColor: 'var(--color-primario)',
                                },
                            }}>
                            <Stack 
                                className='text-center'>
                                <StackItem>
                                    <FontIcon 
                                        aria-label="Clock" 
                                        iconName="Clock"
                                        className='text-white'
                                        style={{
                                            fontSize: 20,
                                        }} />
                                </StackItem>
                                <StackItem>
                                    <Text
                                        className='text-white'>
                                        {
                                            unfinishedWorkPart ? (
                                                duration(unfinishedWorkPart.Inicio)
                                            ) : (
                                                `00:00 h`
                                            )
                                        }
                                    </Text>
                                </StackItem>
                            </Stack>
                        </StackItem>
                    </Stack>
                </StackItem>
                <StackItem 
                    className='my-2'
                    styles={{
                        root: {
                            overflowWrap: 'anywhere',
                        },
                    }}>
                    <div 
                        dangerouslySetInnerHTML={{ __html: props.work.Observaciones }}></div>
                </StackItem>
                <StackItem 
                    className='my-2'>
                    {
                        props.workParts.map((wp: ItemRegistroTiempo) => (
                            <Stack 
                                horizontal={true}
                                key={wp.Id}
                                className="my-2 align-items-center justify-content-between bg-white">
                                <StackItem
                                    className='flex-fill'>
                                    <Stack 
                                        horizontal={true}
                                        className="my-2 align-items-center justify-content-between bg-white"
                                        styles={{
                                            root: {
                                                cursor: 'pointer',
                                            },
                                        }}
                                        onClick={() => onWorkPartEdit(wp)}>
                                        <StackItem 
                                            className='p-2'>
                                            <Image
                                                src={wp.Fin ? pauseIcon : playIcon}
                                                height={32}
                                                width={32} />
                                        </StackItem>
                                        <StackItem
                                            className='d-flex flex-column flex-fill p-2'>
                                            <Text>
                                                {moment(wp.Inicio).format("DD-MM HH:mm")}
                                            </Text>
                                            <Text
                                                styles={{
                                                    root: {
                                                        color: '#A0A0A0',
                                                    },
                                                }}>
                                                {wp.Descripcion}
                                            </Text>
                                        </StackItem>
                                        <StackItem
                                            className='p-2'>
                                            {duration(wp.Inicio, wp.Fin)}
                                        </StackItem>
                                    </Stack>
                                </StackItem>
                                <StackItem
                                    className='px-2'>
                                    <ButtonPrimaryRoundedComponent
                                        icon='ChromeClose'
                                        onClick={() => onWorkPartDelete(wp)} />
                                </StackItem>
                            </Stack>
                        ))
                    }
                </StackItem>
                <StackItem
                    className='my-2 justify-content-end d-flex'>
                    <ButtonPrimaryRoundedComponent
                        icon="add"
                        onClick={() => setWorkPartOpen(true)} />
                </StackItem>
                <StackItem
                    className='my-2'>
                    <Stack
                        horizontal={true}
                        className='d-flex'>
                        <StackItem
                            className='flex-fill px-1'>
                            <ButtonWhiteComponent
                                className='w-100'
                                text='ADJUNTOS'
                                onClick={() => setWorkDocumentListOpen(true)} />
                        </StackItem>
                        <StackItem
                            className='flex-fill px-1'>
                            {
                                !props.work.FechaFinalizacion ? (
                                    <ButtonBlueComponent
                                        className='w-100'
                                        text='FINALIZAR'
                                        onClick={() => setWorkPartFinishOpen(true)} />
                                ) : (
                                    <></>
                                )
                            }
                        </StackItem>
                    </Stack>
                </StackItem>
                <StackItem>
                    <ErrorMessageComponent message={error} />
                </StackItem>
            </Stack>
            {
                workPartOpen ? (
                    <WorkPartCreateComponent
                        work={props.work}
                        onClose={() => setWorkPartOpen(false)}
                        onWorkPartCreatedOrEdited={props.onWorkPartCreatedOrEdited}
                        unfinishedWorkPart={unfinishedWorkPart} />
                ) : (
                    null
                )
            }
            {
                selectedWorkPart ? (
                    <WorkPartCreateComponent
                        work={props.work}
                        workPart={selectedWorkPart}
                        onClose={() => setSelectedWorkPart(undefined)}
                        onWorkPartCreatedOrEdited={props.onWorkPartCreatedOrEdited}
                        unfinishedWorkPart={unfinishedWorkPart} />
                ) : (
                    null
                )
            }
            {
                workDocumentListOpen ? (
                    <WorkDocumentListComponent
                        work={props.work}
                        workDocuments={workDocuments}
                        onClose={() => setWorkDocumentListOpen(false)}
                        workDocumentCreateOpen={workDocumentCreateOpen}
                        onWorkDocumentCreate={() => setWorkDocumentCreateOpen(true)}
                        onWorkDocumentDeleted={onWorkDocumentCreatedOrEdited} />
                ) : (
                    null
                )
            }
            {
                workDocumentCreateOpen ? (
                    <WorkDocumentCreateComponent
                        work={props.work}
                        onClose={() => setWorkDocumentCreateOpen(false)}
                        onWorkDocumentCreated={onWorkDocumentCreatedOrEdited} />
                ) : (
                    null
                )
            }
            {
                !props.work.FechaFinalizacion && workPartFinishOpen ? (
                    <WorkPartFinishComponent
                        work={props.work}
                        onClose={() => setWorkPartFinishOpen(false)}
                        onWorkPartFinished={props.onWorkFinished}
                        onOpenWorkPartFinishInfo={() => setWorkPartFinishInfoOpen(true)}
                        workPartFinishInfoOpen={workPartFinishInfoOpen}
                        unfinishedWorkPart={unfinishedWorkPart} />
                ) : (
                    null
                )
            }
            {
                workPartFinishInfoOpen ? (
                    <WorkPartFinishInfoComponent
                        work={props.work}
                        onClose={() => setWorkPartFinishInfoOpen(false)} />
                ) : (
                    null
                )
            }
        </>
    );
};

export default WorkReadonlyComponent;
